import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type SupportedLanguage = 'en' | 'es' | 'fr';

interface LanguageContextType {
  currentLanguage: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
  initialLanguage?: SupportedLanguage;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ 
  children, 
  initialLanguage = 'en' 
}) => {
  // Initialize with saved language from localStorage or default
  const getInitialLanguage = (): SupportedLanguage => {
    const saved = localStorage.getItem('preferred-language');
    if (saved && (saved === 'en' || saved === 'es' || saved === 'fr')) {
      return saved as SupportedLanguage;
    }
    return initialLanguage;
  };

  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>(getInitialLanguage);

  // Debug: Log when currentLanguage changes
  useEffect(() => {
    // LanguageContext - currentLanguage changed logged
  }, [currentLanguage]);

  const setLanguage = (language: SupportedLanguage) => {
    // LanguageContext - Setting language logged
    setCurrentLanguage(language);
    // Optionally save to localStorage for persistence
    localStorage.setItem('preferred-language', language);
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 